import {Component, OnDestroy, OnInit} from '@angular/core';
import {InfiniteScrollCustomEvent, ModalController} from '@ionic/angular';
import {Subject, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {OrdinispedizioniService} from '../../../_services/ordinispedizioni.service';
import {OrganizzazioniService} from '../../../_services/organizzazioni.service';
import {MovimentiService} from '../../../_services/movimenti.service';
import {SaveMessage} from '../../../_helpers/save-message';
import {OrdSped} from '../../../_models/OrdSped';
import {OrdSpedBox} from '../../../_models/OrdSpedBox';
import {OrdSpedEsolver} from '../../../_models/OrdSpedEsolver';
import {Contenitore} from '../../../_models/Contenitore';
import {SearchResult} from '../../../_models/SearchResult';
import {erroreSpostamento, SPOSTA_ESOLVER_PARZIALE, SpostaColloEsito} from '../../../_models/spedizioni/sposta-collo';

export type ModalitaSposta = 'collo' | 'bancale';
export type SceltaBancale = 'esistente' | 'nuovo';
// Ruolo di chiusura quando eSOLVER ha rifiutato a metà: la pagina ricarica i colli senza messaggio di successo.
export const RUOLO_SPOSTA_PARZIALE = 'parziale';

/**
 * Feature 148: sposta un collo, o un bancale con tutti i suoi colli, su un'altra riga di spedizione.
 * Le destinazioni arrivano dagli endpoint esistenti; il nuovo bancale si crea con `app-box-input`,
 * come nella card Bancale della spedizione manuale.
 * Feature 156: in modalità bancale si scelgono i colli (tutti di default); il collo, o una selezione parziale,
 * può cambiare bancale sulla stessa riga; le destinazioni sono tutte le righe dello stesso articolo e variante,
 * escluse le spedizioni terminate, caricate a pagine con infinite scroll.
 */
@Component({
  selector: 'app-spedizione-sposta-collo',
  templateUrl: './spedizione-sposta-collo.component.html',
  standalone: false
})
export class SpedizioneSpostaColloComponent implements OnInit, OnDestroy {
  modalita: ModalitaSposta = 'collo';
  ordSped: OrdSped;
  box?: OrdSpedBox;
  codBancale?: string;
  colli: OrdSpedBox[] = [];

  selezionati = new Set<string>();
  mostraColli = false;

  search = '';
  destinazioni: OrdSpedEsolver[] = [];
  // Riga aperta, costruita da ordSped: sempre in testa quando il cambio bancale è ammesso (FR-024)
  rigaSorgente?: OrdSpedEsolver;
  loadMore = false;
  private page = 0;
  private readonly count = 20;
  private ricerca = 0;
  private bancaliDi?: OrdSpedEsolver;
  destinazione?: OrdSpedEsolver;
  bancali: Contenitore[] = [];
  sceltaBancale: SceltaBancale = 'esistente';
  codBancaleDest = '';
  formatoNuovo = 0;
  inviaEsolver = true;
  loading = false;
  saving = false;
  errore = '';
  private readonly search$ = new Subject<string>();
  private readonly subscription = new Subscription();

  constructor(
    private spedizioniService: OrdinispedizioniService,
    private organizzazioniService: OrganizzazioniService,
    private movimentiService: MovimentiService,
    private modalCtrl: ModalController,
    private saveMessage: SaveMessage) { }

  get colliScelti(): OrdSpedBox[] {
    return this.colli.filter(c => this.selezionati.has(c.boxCode));
  }

  // Bancale con una parte dei colli: il bancale resta, i colli scelti vanno su un bancale della destinazione.
  get parziale(): boolean {
    const scelti = this.colliScelti.length;
    return this.modalita === 'bancale' && scelti > 0 && scelti < this.colli.length;
  }

  // Bancale con tutti i colli verso la sua stessa riga: i colli vanno su un altro bancale della spedizione.
  get unisci(): boolean {
    const d = this.destinazione;
    return this.modalita === 'bancale' && !this.parziale && !!d && this.isRigaSorgente(d);
  }

  // Collo singolo, selezione parziale o unione: si sceglie il bancale di destinazione, anche sulla stessa riga.
  get scegliBancale(): boolean {
    return this.modalita === 'collo' || this.parziale || this.unisci;
  }

  get elencoDestinazioni(): OrdSpedEsolver[] {
    return this.rigaSorgente ? [this.rigaSorgente, ...this.destinazioni] : this.destinazioni;
  }

  get qtaSpostata(): number {
    return this.modalita === 'collo'
      ? this.box?.boxQta ?? 0
      : this.colliScelti.reduce((tot, c) => tot + (c.boxQta ?? 0), 0);
  }

  get rifDest(): string {
    const d = this.destinazione;
    return d ? `${Math.trunc(d.periodoRifNumeraz)}.${d.idDocumento}.${d.idRiga}` : '';
  }

  get canConfirm(): boolean {
    if (!this.destinazione || this.saving) return false;
    if (this.modalita === 'bancale' && this.colli.length > 0 && this.selezionati.size === 0) return false;
    return !this.scegliBancale || !!this.codBancaleDest;
  }

  ngOnInit() {
    this.selezionati = new Set(this.colli.map(c => c.boxCode));
    this.rigaSorgente = this.daOrdSped();
    this.subscription.add(this.search$.pipe(debounceTime(300))
      .subscribe(testo => void this.cercaDestinazioni(testo)));
    void this.cercaDestinazioni('');
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onSearch = (testo: string) => this.search$.next(testo ?? '');

  // Pagina 0 alla ricerca, pagina successiva con l'infinite scroll; una risposta superata da una ricerca più
  // recente viene scartata.
  async cercaDestinazioni(testo: string, altre = false) {
    this.loading = true;
    const org = this.organizzazioniService.currentOrganizzazioneValue;
    const ricerca = ++this.ricerca;
    const page = altre ? this.page + 1 : 0;
    try {
      const result: SearchResult<OrdSpedEsolver> = await this.spedizioniService.getSpedizioniEsolver(
        this.organizzazioniService.organizzazioneId, org?.codiceGruppo, org?.codice, testo, page, this.count, '', true,
        -1, false, [], true, -1, this.ordSped.codArt, '', true, this.ordSped.varianteArt ?? '', true);
      if (ricerca !== this.ricerca) return;
      const righe = (result?.result ?? []).filter(d => !this.isRigaSorgente(d));
      this.destinazioni = altre ? [...this.destinazioni, ...righe] : righe;
      this.page = page;
      this.loadMore = (page + 1) * this.count < (result?.totalCount ?? 0);
    } catch {
      this.saveMessage.messageError('Ci sono problemi con il caricamento delle spedizioni.');
    } finally {
      this.loading = false;
    }
  }

  caricaAltre = async (event: InfiniteScrollCustomEvent) => {
    await this.cercaDestinazioni(this.search, true);
    await event.target.complete();
  };

  async selezionaDestinazione(destinazione: OrdSpedEsolver) {
    this.destinazione = destinazione;
    this.codBancaleDest = '';
    this.bancali = [];
    this.bancaliDi = undefined;
    this.errore = '';
    if (this.scegliBancale) await this.caricaBancali();
  }

  toggleCollo = (codice: string) => {
    if (!this.selezionati.delete(codice)) this.selezionati.add(codice);
    return this.selezioneCambiata();
  };

  selezionaTutti = (tutti: boolean) => {
    this.selezionati = new Set(tutti ? this.colli.map(c => c.boxCode) : []);
    return this.selezioneCambiata();
  };

  // Con una selezione parziale serve il bancale di destinazione.
  private async selezioneCambiata() {
    if (this.destinazione && this.scegliBancale && this.bancaliDi !== this.destinazione) await this.caricaBancali();
  }

  setSceltaBancale(scelta: SceltaBancale) {
    this.sceltaBancale = scelta;
    this.codBancaleDest = '';
  }

  // Codice emesso da app-box-input dopo la creazione del nuovo bancale.
  onNuovoBancale = (codice: string) => this.codBancaleDest = codice ?? '';

  async conferma() {
    if (!this.canConfirm) return;
    this.saving = true;
    this.errore = '';
    try {
      this.modalCtrl.dismiss(await this.invia(), 'confirm');
    } catch (err) {
      const {errorType, error} = erroreSpostamento(err);
      this.errore = error ?? 'Spostamento non riuscito.';
      this.saveMessage.messageError(this.errore);
      // eSOLVER ha rifiutato a metà: una parte dei colli è già spostata, il dialog non può ripetere lo spostamento.
      if (errorType === SPOSTA_ESOLVER_PARZIALE) this.modalCtrl.dismiss(null, RUOLO_SPOSTA_PARZIALE);
    } finally {
      this.saving = false;
    }
  }

  cancel = () => this.modalCtrl.dismiss(null, 'cancel');

  private invia(): Promise<SpostaColloEsito> {
    const orgId = this.organizzazioniService.organizzazioneId;
    const dest = this.destinazione as OrdSpedEsolver;
    if (this.modalita === 'bancale') {
      // Tutti selezionati verso un'altra riga: codiciColli vuoto = bancale intero, compresi i suoi colli su altre
      // righe (FR-011). Selezione parziale o unione sulla stessa riga: i colli scelti sul bancale di destinazione.
      const perColli = this.scegliBancale;
      return this.spedizioniService.spostaBancale(orgId, {
        idDocDest: dest.idDocumento, idRigDest: dest.idRiga, codBancale: this.codBancale ?? '',
        inviaEsolver: this.inviaEsolver,
        codiciColli: perColli ? this.colliScelti.map(c => c.boxCode) : [],
        codBancaleDest: perColli ? this.codBancaleDest : undefined,
        nuovoBancale: perColli && this.sceltaBancale === 'nuovo'
      });
    }
    return this.spedizioniService.spostaCollo(orgId, {
      idDocDest: dest.idDocumento, idRigDest: dest.idRiga, codiceCollo: this.box?.boxCode ?? '',
      codBancaleDest: this.codBancaleDest, nuovoBancale: this.sceltaBancale === 'nuovo',
      inviaEsolver: this.inviaEsolver
    });
  }

  private async caricaBancali() {
    this.loading = true;
    const destinazione = this.destinazione;
    this.bancaliDi = destinazione;
    // Sulla riga sorgente il bancale attuale non è una destinazione (FR-024).
    const attuale = destinazione && this.isRigaSorgente(destinazione)
      ? (this.modalita === 'collo' ? this.box?.boxPallet : this.codBancale)
      : undefined;
    try {
      const result = await this.movimentiService.getContenitoriFilteredAsync(
        this.organizzazioniService.organizzazioneId, '', 0, 0, 'codice', true, -1, '', this.rifDest, '', '', '', true);
      if (destinazione !== this.destinazione) return;
      this.bancali = (result?.result ?? []).filter(b => !attuale || b.codice !== attuale);
      this.sceltaBancale = this.bancali.length > 0 ? 'esistente' : 'nuovo';
    } catch {
      this.saveMessage.messageError('Ci sono problemi con il caricamento dei bancali.');
    } finally {
      this.loading = false;
    }
  }

  // OrdSped ha i campi usati da template, rifDest e app-box-input.
  private daOrdSped(): OrdSpedEsolver | undefined {
    const o = this.ordSped;
    if (!o) return undefined;
    return {
      idDocumento: o.idDocumento, idRiga: o.idRiga, numRegistraz: o.numRegistraz,
      periodoRifNumeraz: o.periodoRifNumeraz, codArt: o.codArt, varianteArt: o.varianteArt,
      spedRagSoc1: o.spedRagSoc1, qtaDaSpedire: o.qtaDaSpedire, unitaMisura: o.unitaMisura,
      dataSpedizione: o.dataSpedizione, dataConsegnaConferm: o.dataConsegnaConferm, spedCodCliFor: o.spedCodCliFor
    } as OrdSpedEsolver;
  }

  private isRigaSorgente = (d: OrdSpedEsolver) =>
    d.idDocumento === this.ordSped.idDocumento && d.idRiga === this.ordSped.idRiga;
}
